import { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import localData from 'utils/localData';
import api from 'utils/api';
// components and parts
import { PageLoader, PageError } from 'components';
import { TABLE_TYPE } from 'components/PayrollTable/constants';
import { DOLPHIN_API, KENSHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';
import {
  getFirstDateOfTheSameMonth,
  getLastDateOfTheSameMonth,
} from 'utils/dateTime';
import { useSnackbar } from 'notistack';
import PayrollTable from 'components/PayrollTable';
import { getSummaryTableData, summaryTableColumns } from './summaryTable';
import { detailsTableColumns, getDetailedTableData } from './detailsTable';
import { useNavigate, Outlet } from 'react-router-dom';
import { kenshinTableColumns, getKenshinTableData } from './kenshinTable';
import StarLabel from 'components/PayrollTable/components/StarLabel';
import {
  updateBillForSentTasks,
  updateBillForSentTasksKenshin,
} from '../shared/api';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
import logger from 'utils/logger';
import { isCurrentUserTaskManagerKenshin } from 'utils/privilege';
import { parseBillingMonth } from 'views/Billing/shared/utils';

const log = logger.createLogger('DashboardBillingForSentTasks');

const DashboardBillingForSentTasks = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const doesHaveKenshinPermission = isCurrentUserTaskManagerKenshin();

  const [loaded, setLoaded] = useState(false);
  const [loadedKenshin, setLoadedKenshin] = useState(
    doesHaveKenshinPermission ? false : true
  );
  const [ifError, setIfError] = useState(false);

  const currentDate = new Date();

  const firstDate = getFirstDateOfTheSameMonth(currentDate);
  const lastDate = getLastDateOfTheSameMonth(currentDate);
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.BILLING_START_DATE);
    return saved ? new Date(saved) : firstDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.BILLING_END_DATE);
    return saved ? new Date(saved) : lastDate;
  });
  const [tableType, setTableType] = useState(TABLE_TYPE.SUMMARY);
  const [selectedDstOrgId, setSelectedDstOrgId] = useState(-1);
  const [selectedDstOrgName, setSelectedDstOrgName] = useState('');
  const [selectedBillingMonth, setSelectedBillingMonth] = useState('');
  const [columns, setColumns] = useState([]);
  const [billData, setBillData] = useState([]);
  const [billDataKenshin, setBillDataKenshin] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [updateApi, setUpdateApi] = useState(null);
  const [refreshTable, setRefreshTable] = useState(0);

  const srcOrgId = localData.get(USER_DATA.CURRENT_ORGANIZATION_ID);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.BILLING_START_DATE,
      selectedStartDate.toISOString()
    );
  }, [selectedStartDate]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.BILLING_END_DATE,
      selectedEndDate.toISOString()
    );
  }, [selectedEndDate]);

  useEffect(() => {
    const apiVariables = {
      params: {
        organizationId: parseInt(srcOrgId),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    const kenshinApiVariables = {
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        username: localData.get(USER_DATA.USERNAME),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    api
      .get(DOLPHIN_API.BILLS_SENT_TASKS, apiVariables)
      .then(async (data) => {
        const { bills } = data;
        setBillData(bills);
        setLoaded(true);
      })
      .catch((error) => {
        log.error(error);
        setIfError(true);
      });

    if (doesHaveKenshinPermission) {
      api
        .get(KENSHIN_API.BILLS_SENT_ISSSUES, kenshinApiVariables)
        .then(async (data) => {
          const { bills } = data;
          setBillDataKenshin(bills);
          setLoadedKenshin(true);
        })
        .catch((error) => {
          log.error(error);
          setIfError(true);
        });
    }
  }, [selectedStartDate, selectedEndDate, srcOrgId, refreshTable]);

  useEffect(() => {
    if (loaded && loadedKenshin) {
      let columns = [];
      let data = [];
      if (tableType === TABLE_TYPE.SUMMARY) {
        columns = summaryTableColumns(
          setSelectedDstOrgName,
          setSelectedDstOrgId,
          setSelectedBillingMonth,
          setTableType,
          billData,
          billDataKenshin,
          doesHaveKenshinPermission
        );
        data = getSummaryTableData(billData, billDataKenshin);
        setUpdateApi(null);
      } else if (tableType === TABLE_TYPE.DETAILED) {
        columns = detailsTableColumns(openSeimitsuIssue);
        data = getDetailedTableData(
          billData,
          selectedDstOrgId,
          selectedBillingMonth
        );
        setUpdateApi(() => updateBillForSentTasks);
      } else if (tableType === TABLE_TYPE.DETAILED_KENSHIN) {
        columns = kenshinTableColumns(openKenshinIssue);
        data = getKenshinTableData(
          billDataKenshin,
          selectedDstOrgId,
          selectedBillingMonth
        );
        setUpdateApi(() => updateBillForSentTasksKenshin);
      }
      setTableData(data);
      setColumns(columns);
    }
  }, [loaded, loadedKenshin, tableType, billData, billDataKenshin]);

  if (!(loaded && loadedKenshin)) {
    return <PageLoader />;
  }
  if (ifError) {
    return <PageError />;
  }

  const refreshTableData = () => {
    if (selectedEndDate.toDateString() === new Date().toDateString()) {
      setSelectedEndDate(new Date());
    }
    setRefreshTable(refreshTable + 1);
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const openSeimitsuIssue = (issueId) => {
    navigate(`/dashboard/billing/sent-billing/seimitsu/issues/${issueId}`);
  };

  const openKenshinIssue = (issueId) => {
    navigate(`/dashboard/billing/sent-billing/kenshin/issues/${issueId}`);
  };

  const StarLabelComponent = (table) => {
    return (
      <StarLabel
        updateBillStatus={updateApi}
        table={table}
        refreshTableData={refreshTableData}
      />
    );
  };

  return (
    <div>
      <PayrollTable
        columns={columns}
        data={tableData}
        tableTitle={intl.get('billing_for_sent_cases_text_sent_cases')}
        timeRangeStart={selectedStartDate}
        timeRangeEnd={selectedEndDate}
        updateTimeRangeStart={setSelectedStartDate}
        updateTimeRangeEnd={setSelectedEndDate}
        handleRefreshClick={refreshTableData}
        subtitle={`[${parseBillingMonth(
          selectedBillingMonth
        )}] ${selectedDstOrgName}`}
        setTableType={setTableType}
        tableType={tableType}
        multiSelectComponent={StarLabelComponent}
        enableRowSelection={tableType === TABLE_TYPE.SUMMARY ? false : true}
      />
      <Outlet context={{ doctors: [], updateLocalProjectIssues: () => {} }} />
    </div>
  );
};

export default DashboardBillingForSentTasks;
